import styles from './styles';

import type { PropsWithChildren } from 'react';
import React, { useEffect } from 'react';

import type { IconDefinition } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { withStyles } from '@material-ui/core';
import type { WithStyles } from '@material-ui/core';
import { Tabs } from '@zinc/ui';
import useMountEffect from 'bz-utilities/hooks/mount-effect';
import { useLocation } from 'react-router';
import { cache } from 'tm3-core/cache';
import { TabEvent } from '../real-sub-header';

export interface IProps extends PropsWithChildren, WithStyles<typeof styles> {
  className?: string;
  'data-testid'?: string;
  disabled?: boolean;
  isActive?: boolean;
  dataTestId?: string;
  icon: IconDefinition;
  onClick: (e: React.MouseEvent<HTMLDivElement>) => void;
  target?: string;
}

const Tab: React.FC<IProps> = ({
  children,
  className = '',
  classes,
  'data-testid': dataTest,
  disabled,
  isActive,
  dataTestId,
  icon,
  onClick: rootOnClick,
  target
}) => {
  const rootClass = React.useMemo<string>(() => {
    const clazzes = [classes.tab];

    if (className) {
      clazzes.push(className);
    }

    if (isActive) {
      clazzes.push('active');
    }

    if (disabled) {
      clazzes.push('disabled');
    }

    return clazzes.join(' ');
  }, [classes, className, isActive, disabled]);

  const onClick = React.useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!disabled) {
        rootOnClick(e);
      }
    },
    [disabled, rootOnClick]
  );

  useEffect(() => {
    if (isActive) {
      cache.mediator.publish(new TabEvent(target || dataTestId || (children as string) || 'tab'));
    }
  }, [isActive, target, dataTestId]);

  if (disabled) return null; //not sure on use case, but disabled tabs aren't supported in reshaped
  return (
    <Tabs.Item
      value={target || dataTestId || (children as string) || 'tab'}
      attributes={{ onClick, 'data-testid': dataTestId }}
      icon={icon && <FontAwesomeIcon icon={icon} />}
    >
      {children}
    </Tabs.Item>
  );
};

export default withStyles(styles)(Tab);
